import { untaggedMessage, type OpenErrorKind } from '@ims/local'
import { tenantApi } from '@ims/remote'
import { Button, Card, Field, Input, Modal, Switch, cx, useToast } from '@ims/ui'
import { Building2, LockKeyhole, MonitorSmartphone, RefreshCw } from '@ims/ui/icons'
import { useState, type FormEvent } from 'react'
import { errorMessage, useOnline } from '../hooks.ts'
import { createApi } from '../lib/api.ts'
import type { BusinessType } from '../lib/catalog.ts'
import { KindPicker } from './kind-picker.tsx'
import { useSession } from '../session.tsx'
import { useWorkspace } from '../workspace.tsx'
import { AuthLayout } from './auth.tsx'
import { AppSkeleton } from '../layout/app-skeleton.tsx'
import { clearSignupType, signupType } from '../lib/signup-type.ts'
import type { TaxRegistration } from '../lib/registration.ts'

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

const SLUG = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/

const REGISTRATIONS: { id: TaxRegistration; label: string; note: string }[] = [
  { id: 'pan', label: 'PAN only', note: 'No VAT on your bills' },
  { id: 'vat', label: 'VAT registered', note: '13% VAT shown on bills' },
]

/** The limit on owned businesses in words, not "Max_owned_tenants (limit 1)". */
function createErrorText(error: unknown): string {
  const text = error instanceof Error ? error.message : String(error)
  if (/needs_paid_plan/.test(text))
    return 'You can add another business once one of yours is on a paid plan.'
  if (/max_owned_tenants/.test(text))
    return 'Your account has all the businesses it can have. Email or call us if you need more.'
  return errorMessage(error)
}

/**
 * The new-business form: name, short name and what it sells. Used by the
 * first-run screen and by "Create a new business" in the business switcher;
 * `onCreated` gets the new id once the server has made it.
 */
export function CreateBusinessForm({
  onCreated,
  submitLabel = 'Create business',
}: {
  onCreated: (tenantId: string, name: string) => Promise<void>
  submitLabel?: string
}) {
  const { platform } = useSession()
  const online = useOnline()
  const toast = useToast()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugEdited, setSlugEdited] = useState(false)
  // Up to three kinds, the main one first. Someone who came from a business page of the
  // site starts with that kind chosen.
  const [kinds, setKinds] = useState<BusinessType[]>(() => {
    const from = signupType()
    return from ? [from] : []
  })
  // Null until touched: on when a workshop is among the kinds.
  const [services, setServices] = useState<boolean | null>(null)
  const servicesOn = services ?? kinds.includes('workshop')
  const [registration, setRegistration] = useState<TaxRegistration>('pan')
  const [taxNumber, setTaxNumber] = useState('')
  const [busy, setBusy] = useState(false)
  // On a phone the short name is a line with "Edit": it fills itself from the name, and
  // the box opens when asked for or when what it made cannot be used.
  const [slugOpen, setSlugOpen] = useState(false)
  const slugShown = slugOpen || (name.trim() !== '' && !SLUG.test(slug))

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (taxNumber && taxNumber.length !== 9) {
      toast.error(`A ${registration === 'vat' ? 'VAT' : 'PAN'} number has 9 digits`)
      return
    }
    setBusy(true)
    try {
      // Nothing ticked is an "Other shop".
      const [main = 'general', ...others] = kinds
      const { tenant_id } = await tenantApi(platform.supabase).createTenant(
        name.trim(),
        slug,
        main,
        { taxRegistration: registration, panVatNumber: taxNumber || null },
        {
          others,
          // Sent only when it differs from the main kind's default (on for a workshop).
          providesServices:
            main === 'restaurant' || servicesOn === (main === 'workshop') ? null : servicesOn,
        },
      )
      clearSignupType()
      // The welcome email: the server sends it once, for an account's first business.
      // Creating a business never waits for it or fails because of it.
      void createApi(platform.apiUrl, platform.supabase)
        .welcome(tenant_id)
        .catch(() => undefined)
      await onCreated(tenant_id, name.trim())
    } catch (error) {
      toast.error(createErrorText(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {!online && (
        <p className="mb-4 rounded-xl bg-warning-light p-3 text-sm text-warning">
          Connect to the internet to create a business.
        </p>
      )}
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-x-3 gap-y-4 sm:grid-cols-[1.25fr_1fr]">
          <Field label="Business name">
            {(id) => (
              <Input
                id={id}
                value={name}
                required
                maxLength={120}
                onChange={(e) => {
                  setName(e.target.value)
                  if (!slugEdited) setSlug(slugify(e.target.value))
                }}
              />
            )}
          </Field>
          {!slugShown && (
            <p className="-mt-2 text-xs text-muted sm:hidden">
              Link name:{' '}
              <span className="font-semibold text-ink">{slug || 'made from the name'}</span>
              {' · '}
              <button
                type="button"
                className="font-semibold text-primary hover:underline"
                onClick={() => setSlugOpen(true)}
              >
                Edit link name
              </button>
            </p>
          )}
          <Field
            label="Short name"
            hint="Lowercase letters, numbers and dashes. Used in links."
            className={cx(!slugShown && 'max-sm:hidden')}
          >
            {(id) => (
              <Input
                id={id}
                value={slug}
                required
                pattern="[a-z0-9][a-z0-9\-]{1,38}[a-z0-9]"
                onChange={(e) => {
                  setSlugEdited(true)
                  setSlug(e.target.value.toLowerCase())
                }}
              />
            )}
          </Field>
        </div>
        <fieldset>
          <legend className="mb-0.5 text-sm font-medium">What do you sell?</legend>
          <p className="mb-2 text-xs text-muted">
            Pick up to three. The first is your main kind. You can change it later in Settings.
          </p>
          <KindPicker kinds={kinds} onChange={setKinds} />
          {kinds[0] !== 'restaurant' && (
            <Switch
              className="mt-3"
              label="We also provide services"
              description="Repairs, fitting, treatments, soil tests: sold by the job or the hour, never counted in stock."
              checked={servicesOn}
              onChange={(e) => setServices(e.target.checked)}
            />
          )}
        </fieldset>
        <fieldset>
          <legend className="mb-0.5 text-sm font-medium">Are you registered for VAT?</legend>
          <p className="mb-2 text-xs text-muted">
            This decides what your bills look like. You can change it later in Settings.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {REGISTRATIONS.map((r) => (
              <label
                key={r.id}
                className={cx(
                  'flex cursor-pointer gap-2.5 rounded-xl border p-2.5 text-sm transition-colors has-focus-visible:ring-2 has-focus-visible:ring-primary/30',
                  registration === r.id
                    ? 'border-primary bg-primary-light'
                    : 'border-border hover:border-primary/40',
                )}
              >
                <input
                  type="radio"
                  name="tax_registration"
                  value={r.id}
                  checked={registration === r.id}
                  onChange={() => setRegistration(r.id)}
                  className="sr-only"
                />
                <span
                  aria-hidden
                  className={cx(
                    'mt-0.5 size-[18px] shrink-0 rounded-full bg-surface',
                    registration === r.id
                      ? 'border-[5px] border-primary'
                      : 'border-[1.5px] border-subtle',
                  )}
                />
                <span className="min-w-0">
                  <span className="block font-medium">{r.label}</span>
                  <span className="block text-xs text-muted">{r.note}</span>
                </span>
              </label>
            ))}
          </div>
          {registration === 'vat' && (
            <p className="mt-2 rounded-xl bg-info-light p-3 text-xs">
              <span className="font-medium">
                This app is not yet IRD-approved billing software.
              </span>{' '}
              Until it is, keep issuing your official VAT bill from your IRD-approved bill book or
              software.
            </p>
          )}
        </fieldset>
        <Field
          label={`${registration === 'vat' ? 'VAT' : 'PAN'} number (optional)`}
          hint="9 digits. Printed on your bills; you can add it later."
        >
          {(id) => (
            <Input
              id={id}
              value={taxNumber}
              inputMode="numeric"
              autoComplete="off"
              maxLength={11}
              onChange={(e) => setTaxNumber(e.target.value.replace(/\D/g, '').slice(0, 9))}
            />
          )}
        </Field>
        <Button type="submit" className="w-full" loading={busy} disabled={!online}>
          {submitLabel}
        </Button>
      </form>
    </>
  )
}

export function CreateBusinessScreen() {
  const { refreshMemberships, selectTenant, signOut } = useWorkspace()
  return (
    <AuthLayout
      poster="trial"
      step={2}
      wide
      top={
        <button type="button" className="hover:underline" onClick={() => void signOut()}>
          Sign out
        </button>
      }
    >
      <h1 className="mb-1 text-2xl font-extrabold tracking-tight">Set up your business</h1>
      <p className="mb-5 text-sm text-muted">
        A main store and a billing counter are made for you, with 90 days free. Invited by someone?
        Open the invite link from your email instead.
      </p>
      <CreateBusinessForm
        onCreated={async (tenantId) => {
          await refreshMemberships()
          selectTenant(tenantId)
        }}
      />
    </AuthLayout>
  )
}

/** "Create a new business" from the business switcher (owners only). */
export function CreateBusinessDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { refreshMemberships, selectTenant } = useWorkspace()
  const toast = useToast()
  return (
    <Modal open={open} onClose={onClose} title="Create a new business" size="md">
      <p className="mb-4 text-sm text-muted">
        A separate business with its own products, bills and staff, and a 14-day free trial of its
        own. You can switch between your businesses from the top-left of the app.
      </p>
      <CreateBusinessForm
        onCreated={async (tenantId, name) => {
          await refreshMemberships()
          onClose()
          selectTenant(tenantId)
          toast.success(`${name} created`)
        }}
      />
    </Modal>
  )
}

export function TenantPickerScreen() {
  const { memberships, selectTenant } = useWorkspace()
  return (
    <AuthLayout>
      <Card className="p-2">
        <h1 className="px-4 pb-2 pt-3 text-lg font-semibold">Choose a business</h1>
        {memberships.map((m) => (
          <button
            key={m.tenant_id}
            type="button"
            onClick={() => selectTenant(m.tenant_id)}
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left hover:bg-soft"
          >
            <Building2 className="size-5 text-primary" />
            <div className="flex-1">
              <div className="font-medium">{m.tenant.name}</div>
              <div className="text-xs capitalize text-muted">{m.role}</div>
            </div>
          </button>
        ))}
      </Card>
    </AuthLayout>
  )
}

/** What went wrong, in words, by the kind the local store gave (docs/plans/local-db-pools.md). */
const OPEN_ERROR_WORDS: Record<OpenErrorKind, string> = {
  busy: 'Sano Godam has this business open in another tab or window. Close it, then try again.',
  full: 'This device is short of space for Sano Godam’s offline copy. Free some space, then try again.',
  unsupported:
    'This browser needs Origin Private File System support (current Chrome, Edge, Firefox or Safari).',
  other: 'Something stopped the offline copy of this business from opening.',
}

export function StoreGate() {
  const { storeState, takeOver, reopen, current } = useWorkspace()
  const { platform } = useSession()
  if (storeState.status === 'opening' || storeState.status === 'none')
    return <AppSkeleton label={`Opening ${current?.tenant.name ?? ''}`} />
  if (storeState.status === 'locked' || storeState.status === 'handedOver') {
    const handedOver = storeState.status === 'handedOver'
    return (
      <AuthLayout>
        <Card className="p-6 text-center">
          <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-primary-light text-primary">
            {handedOver ? (
              <MonitorSmartphone className="size-6" />
            ) : (
              <LockKeyhole className="size-6" />
            )}
          </div>
          <h1 className="mb-1 font-semibold">
            {handedOver ? 'Opened in another tab' : 'Open in another tab'}
          </h1>
          <p className="mb-5 text-sm text-muted">
            {current?.tenant.name} can be open in one tab at a time on this device, so bills and
            stock stay consistent.
          </p>
          <Button className="w-full" onClick={takeOver}>
            Use here
          </Button>
        </Card>
      </AuthLayout>
    )
  }
  if (storeState.status === 'error') {
    const said = OPEN_ERROR_WORDS[storeState.kind]
    return (
      <AuthLayout>
        <Card className="p-6">
          <h1 className="mb-2 font-semibold">Couldn’t open the local database</h1>
          <p className="mb-3 text-sm text-ink">{said}</p>
          <p className="mb-4 break-words text-xs text-muted">
            {untaggedMessage(storeState.message)}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button icon={<RefreshCw className="size-4" />} onClick={reopen}>
              Try again
            </Button>
            {/* A reload frees every file the app holds; offered once Try again has failed. */}
            {storeState.tries > 0 && platform.reload && (
              <Button variant="secondary" onClick={platform.reload}>
                Reload the app
              </Button>
            )}
          </div>
        </Card>
      </AuthLayout>
    )
  }
  return null
}
